import { Stack } from '@grubhubprod/gh-cookbook-react';
import React, { useEffect } from 'react';
import { useFilterTagInvites } from 'src/common/features/LineOfCredit/hooks/useFilterTagInvites';
import { IMealCredit } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { useManageMealCreditGroups } from 'src/components/oneCorp/features/MealCredit/hooks/useManageMealCreditGroups';
import { DEFAULT_ERROR_MESSAGE, pushError } from 'src/utilities/notifications';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';
import { getMealCreditGroupAnalyticsEvent } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditAnalytics.utils';
import GroupsTable from './GroupsTable/GroupsTable';
import { sortAscTagInvites } from './Groups.utils';
import { GroupSearchForm } from './GroupSearchForm/GroupSearchForm';

export interface GroupsControllerProps {
  corpId: string;
  mealCreditId: string;
  mealCredit: IMealCredit;
  getMealCreditDetails: () => Promise<void>;
  onTotalResultsChange?: (count: number) => void;
}

export default function GroupsController({
  corpId,
  mealCreditId,
  mealCredit,
  getMealCreditDetails,
  onTotalResultsChange,
}: GroupsControllerProps): JSX.Element {
  const {
    filterTagInvitesBySearch,
    filteredTagInvites,
    isUsingQuery,
    initialTagInvitesLength,
  } = useFilterTagInvites({
    loc: mealCredit,
  });

  const { sendAnalytics } = useSendAnalytics({
    eventcategory: 'meal credit settings',
  });

  const {
    addGroup,
    removeGroup,
    replaceGroup,
    isAdding,
    isRemoving,
    isUpdating,
  } = useManageMealCreditGroups({
    corpId,
    mealCreditId,
    tagInvites: mealCredit.tag_invites,
    onSuccess: handleSuccess,
    onError: async () => {
      pushError(DEFAULT_ERROR_MESSAGE);
    },
  });

  async function handleSuccess(): Promise<void> {
    const eventaction = getMealCreditGroupAnalyticsEvent(isAdding, isUpdating);
    if (eventaction) {
      sendAnalytics({ eventaction });
    }
    await getMealCreditDetails();
  }

  useEffect(() => {
    onTotalResultsChange?.(initialTagInvitesLength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTagInvitesLength]);

  return (
    <>
      <Stack y="md">
        <GroupSearchForm onSubmit={filterTagInvitesBySearch} />
      </Stack>
      <GroupsTable
        tagInvites={mealCredit.tag_invites}
        filteredTagInvites={sortAscTagInvites(filteredTagInvites)}
        initialTagInvitesLength={initialTagInvitesLength}
        hasNoResultsWithQuery={filteredTagInvites.length === 0 && isUsingQuery}
        hasNoResultsWithoutQuery={
          filteredTagInvites.length === 0 && !isUsingQuery
        }
        removeGroup={removeGroup}
        replaceGroup={replaceGroup}
        addGroup={addGroup}
        isAdding={isAdding}
        isRemoving={isRemoving}
        isUpdating={isUpdating}
      />
    </>
  );
}
